/* eslint-disable no-await-in-loop */
import type { Tag } from '@musedrops/shared';
import { fromDbKeys, toDbKeys } from '@musedrops/shared';
import { supabase } from '../services/supabase';

/**
 * Normalize tag name to kebab-case format
 * - Converts to lowercase
 * - Replaces non-alphanumeric characters with hyphens
 * - Removes leading/trailing hyphens
 * - Collapses multiple consecutive hyphens
 */
export function normalizeTagName(tag: string): string {
  return tag
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

/**
 * Validate tag name format (kebab-case)
 */
export function isValidTagName(tag: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(tag);
}

/**
 * Get all tags sorted by tag name
 */
export async function getAllTags(): Promise<Tag[]> {
  const { data, error } = await supabase
    .from('tags')
    .select('*')
    .order('tag', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch tags: ${error.message}`);
  }

  return (data ?? []).map(row =>
    fromDbKeys<Tag>(row as Record<string, unknown>)
  );
}

/**
 * Get single tag by ID
 */
export async function getTagById(id: string): Promise<Tag> {
  const { data, error } = await supabase
    .from('tags')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch tag: ${error.message}`);
  }

  if (!data) {
    throw new Error('Tag not found');
  }

  return fromDbKeys<Tag>(data as Record<string, unknown>);
}

/**
 * Get tag by name (case-insensitive)
 */
export async function getTagByName(tag: string): Promise<Tag | null> {
  const normalized = normalizeTagName(tag);

  const { data, error } = await supabase
    .from('tags')
    .select('*')
    .eq('tag', normalized)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch tag: ${error.message}`);
  }

  return data ? fromDbKeys<Tag>(data as Record<string, unknown>) : null;
}

/**
 * Create new tag with kebab-case validation
 */
export async function createTag(tag: string): Promise<Tag> {
  const normalized = normalizeTagName(tag);

  if (!isValidTagName(normalized)) {
    throw new Error(
      `Invalid tag name: ${tag}. Must be kebab-case (lowercase alphanumeric with hyphens)`
    );
  }

  // Check if tag already exists
  const existing = await getTagByName(normalized);
  if (existing) {
    return existing; // Return existing tag instead of throwing error
  }

  const { data, error } = await supabase
    .from('tags')
    .insert(toDbKeys({ tag: normalized }))
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create tag: ${error.message}`);
  }

  return fromDbKeys<Tag>(data as Record<string, unknown>);
}

/**
 * Update/rename tag with validation
 * Also updates all shows and stories that use this tag
 */
export async function updateTag(id: string, newTag: string): Promise<Tag> {
  const normalized = normalizeTagName(newTag);

  if (!isValidTagName(normalized)) {
    throw new Error(
      `Invalid tag name: ${newTag}. Must be kebab-case (lowercase alphanumeric with hyphens)`
    );
  }

  // Get the old tag name before updating
  const oldTag = await getTagById(id);
  const oldTagName = oldTag.tag;

  // Update the tag record
  const { data, error } = await supabase
    .from('tags')
    .update(toDbKeys({ tag: normalized }))
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update tag: ${error.message}`);
  }

  // Update all shows that contain the old tag
  const { data: shows, error: showsError } = await supabase
    .from('shows')
    .select('id, tags')
    .not('tags', 'is', null);

  if (showsError) {
    throw new Error(`Failed to fetch shows: ${showsError.message}`);
  }

  for (const show of shows ?? []) {
    if (show.tags && show.tags.includes(oldTagName)) {
      const tags = show.tags
        .split(',')
        .map((t: string) => (t === oldTagName ? normalized : t));
      const newTags = tags.join(',');

      await supabase.from('shows').update({ tags: newTags }).eq('id', show.id);
    }
  }

  // Update all stories that contain the old tag
  const { data: stories, error: storiesError } = await supabase
    .from('stories')
    .select('id, tags')
    .not('tags', 'is', null);

  if (storiesError) {
    throw new Error(`Failed to fetch stories: ${storiesError.message}`);
  }

  for (const story of stories ?? []) {
    if (story.tags && story.tags.includes(oldTagName)) {
      const tags = story.tags
        .split(',')
        .map((t: string) => (t === oldTagName ? normalized : t));
      const newTags = tags.join(',');

      await supabase
        .from('stories')
        .update({ tags: newTags })
        .eq('id', story.id);
    }
  }

  return fromDbKeys<Tag>(data as Record<string, unknown>);
}

/**
 * Delete tag (should validate not used in prompts first)
 */
export async function deleteTag(id: string): Promise<void> {
  // First remove tag from all shows and stories
  const tag = await getTagById(id);
  await removeTagFromAllEntities(tag.tag);

  // Then delete the tag record
  const { error } = await supabase.from('tags').delete().eq('id', id);

  if (error) {
    throw new Error(`Failed to delete tag: ${error.message}`);
  }
}

/**
 * Remove tag from all shows and stories
 * Helper for tag deletion
 */
async function removeTagFromAllEntities(tagName: string): Promise<void> {
  // Get all shows with this tag
  const { data: shows, error: showsError } = await supabase
    .from('shows')
    .select('id, tags')
    .not('tags', 'is', null);

  if (showsError) {
    throw new Error(`Failed to fetch shows: ${showsError.message}`);
  }

  // Update shows that contain the tag
  for (const show of shows ?? []) {
    if (show.tags && show.tags.includes(tagName)) {
      const tags = show.tags.split(',').filter((t: string) => t !== tagName);
      const newTags = tags.length > 0 ? tags.join(',') : null;

      await supabase.from('shows').update({ tags: newTags }).eq('id', show.id);
    }
  }

  // Get all stories with this tag
  const { data: stories, error: storiesError } = await supabase
    .from('stories')
    .select('id, tags')
    .not('tags', 'is', null);

  if (storiesError) {
    throw new Error(`Failed to fetch stories: ${storiesError.message}`);
  }

  // Update stories that contain the tag
  for (const story of stories ?? []) {
    if (story.tags && story.tags.includes(tagName)) {
      const tags = story.tags.split(',').filter((t: string) => t !== tagName);
      const newTags = tags.length > 0 ? tags.join(',') : null;

      await supabase
        .from('stories')
        .update({ tags: newTags })
        .eq('id', story.id);
    }
  }
}

/**
 * Get tags used in prompt templates
 * Scans gen_prompts.prompt field for #tag-name patterns
 */
export async function getTagsUsedInPrompts(): Promise<Set<string>> {
  const { data, error } = await supabase.from('gen_prompts').select('prompt');

  if (error) {
    throw new Error(`Failed to fetch prompts: ${error.message}`);
  }

  const tagsInPrompts = new Set<string>();
  const tagPattern = /#([a-z0-9]+(-[a-z0-9]+)*)/g;

  for (const row of data ?? []) {
    if (row.prompt) {
      const matches = row.prompt.matchAll(tagPattern);
      for (const match of matches) {
        tagsInPrompts.add(match[1]); // match[1] is the tag name without #
      }
    }
  }

  return tagsInPrompts;
}

/**
 * Get tags used in prompt templates with their prompt keys
 * Returns a map of tag name to array of prompt keys that use it
 */
export async function getTagsUsedInPromptsWithKeys(): Promise<
  Map<string, string[]>
> {
  const { data, error } = await supabase
    .from('gen_prompts')
    .select('key, prompt');

  if (error) {
    throw new Error(`Failed to fetch prompts: ${error.message}`);
  }

  const tagToPromptKeys = new Map<string, string[]>();
  const tagPattern = /#([a-z0-9]+(-[a-z0-9]+)*)/g;

  for (const row of data ?? []) {
    if (row.prompt) {
      const matches = row.prompt.matchAll(tagPattern);
      for (const match of matches) {
        const tagName = match[1]; // Tag name without #
        const promptKey = row.key;

        if (!tagToPromptKeys.has(tagName)) {
          tagToPromptKeys.set(tagName, []);
        }

        const keys = tagToPromptKeys.get(tagName)!;
        if (!keys.includes(promptKey)) {
          keys.push(promptKey);
        }
      }
    }
  }

  return tagToPromptKeys;
}

/**
 * Get usage counts for a tag (how many shows + stories use it)
 */
export async function getTagUsageCounts(
  tagName: string
): Promise<{ shows: number; stories: number }> {
  // Count shows with this tag
  const { count: showCount, error: showError } = await supabase
    .from('shows')
    .select('*', { count: 'exact', head: true })
    .like('tags', `%${tagName}%`);

  if (showError) {
    throw new Error(`Failed to count shows: ${showError.message}`);
  }

  // Count stories with this tag
  const { count: storyCount, error: storyError } = await supabase
    .from('stories')
    .select('*', { count: 'exact', head: true })
    .like('tags', `%${tagName}%`);

  if (storyError) {
    throw new Error(`Failed to count stories: ${storyError.message}`);
  }

  return {
    shows: showCount ?? 0,
    stories: storyCount ?? 0,
  };
}
